/**
 * NotificationMediaThumbnail
 *
 * The trailing square media thumbnail of a notification row, ported from the
 * `ContentThumbnailPagerWidget` inside a clipped 50x50 box that every
 * media-trailing widget renders (e.g. `notification_content_like_item.dart:130-149`).
 * Builds the thumbnail URL via `MediaUtils.getMediaInfoThumbnailUrl(mediaInfo)`
 * (the `MediaInfoEntity` sibling of `getMediaThumbnailUrl`), with a play overlay
 * for videos. Reuses the shared `ContentThumbnail`.
 */
import React from 'react';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import type { MediaInfoEntity } from '@/domain/entities/info/mediaInfoEntity';

export interface NotificationMediaThumbnailProps {
  mediaInfo: MediaInfoEntity;
}

export const NotificationMediaThumbnail: React.FC<NotificationMediaThumbnailProps> = ({
  mediaInfo,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const isVideo = MediaUtils.mediaInfoIsVideo(mediaInfo);

  return (
    <div
      style={{
        width: responsive.avatar.medium,
        height: responsive.avatar.medium,
        borderRadius: responsive.borderRadius.base,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: theme.backgroundSecondary,
      }}
    >
      <ContentThumbnail thumbnailUrl={MediaUtils.getMediaInfoThumbnailUrl(mediaInfo)} />
      {isVideo && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <AssetIcon
            path="/assets/vectors/play_filled.svg"
            width={responsive.icon.small}
            height={responsive.icon.small}
          />
        </div>
      )}
    </div>
  );
};
